/**
 * Performance optimization utilities for the Komentas widget
 */

/**
 * Lazy load a component when it's needed
 */
export function lazyLoadComponent<T>(
  loader: () => Promise<T>,
  fallback?: () => HTMLElement
): Promise<T> {
  let cached: T | null = null;
  
  return new Promise((resolve, reject) => {
    if (cached) {
      resolve(cached);
      return;
    }

    // Show fallback if provided
    if (fallback) {
      const fallbackElement = fallback();
      document.body.appendChild(fallbackElement);
    }

    loader()
      .then((component) => {
        cached = component;
        resolve(component);
      })
      .catch(reject);
  });
}

/**
 * Debounced function for performance optimization
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  immediate?: boolean
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    
    const callNow = immediate && !timeout;
    
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    
    if (callNow) func(...args);
  };
}

/**
 * Throttle function for limiting API calls
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  
  return function executedFunction(this: any, ...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Intersection Observer for lazy loading content
 */
export class LazyLoader {
  private observer: IntersectionObserver;
  private callbacks = new WeakMap<Element, () => void>();

  constructor(options?: IntersectionObserverInit) {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const callback = this.callbacks.get(entry.target);
          if (callback) {
            callback();
            this.unobserve(entry.target);
          }
        }
      });
    }, {
      rootMargin: '50px',
      threshold: 0.1,
      ...options,
    });
  }

  observe(element: Element, callback: () => void): void {
    this.callbacks.set(element, callback);
    this.observer.observe(element);
  }

  unobserve(element: Element): void {
    this.callbacks.delete(element);
    this.observer.unobserve(element);
  }

  disconnect(): void {
    this.observer.disconnect();
    this.callbacks = new WeakMap();
  }
}

/**
 * Virtual scrolling for large comment lists
 */
export class VirtualScroller {
  private container: HTMLElement;
  private itemHeight: number;
  private visibleCount: number;
  private startIndex = 0;
  private endIndex = 0;
  private items: any[] = [];
  private renderItem: (item: any, index: number) => HTMLElement;

  constructor(
    container: HTMLElement,
    itemHeight: number,
    renderItem: (item: any, index: number) => HTMLElement
  ) {
    this.container = container;
    this.itemHeight = itemHeight;
    this.renderItem = renderItem;
    this.visibleCount = Math.ceil(container.clientHeight / itemHeight) + 2;
    
    this.setupScrollListener();
  }

  setItems(items: any[]): void {
    this.items = items;
    this.updateVisibleItems();
  }

  private setupScrollListener(): void {
    this.container.addEventListener('scroll', throttle(() => {
      this.updateVisibleItems();
    }, 16)); // 60fps
  }

  private updateVisibleItems(): void {
    const scrollTop = this.container.scrollTop;
    const newStartIndex = Math.floor(scrollTop / this.itemHeight);
    const newEndIndex = Math.min(
      newStartIndex + this.visibleCount,
      this.items.length
    );

    if (newStartIndex !== this.startIndex || newEndIndex !== this.endIndex) {
      this.startIndex = newStartIndex;
      this.endIndex = newEndIndex;
      this.render();
    }
  }

  private render(): void {
    const fragment = document.createDocumentFragment();
    
    // Create spacer for items before visible area
    const topSpacer = document.createElement('div');
    topSpacer.style.height = `${this.startIndex * this.itemHeight}px`;
    fragment.appendChild(topSpacer);

    // Render visible items
    for (let i = this.startIndex; i < this.endIndex; i++) {
      const item = this.items[i];
      if (item) {
        const element = this.renderItem(item, i);
        fragment.appendChild(element);
      }
    }

    // Create spacer for items after visible area
    const bottomSpacer = document.createElement('div');
    const remainingItems = this.items.length - this.endIndex;
    bottomSpacer.style.height = `${remainingItems * this.itemHeight}px`;
    fragment.appendChild(bottomSpacer);

    // Replace container content
    this.container.innerHTML = '';
    this.container.appendChild(fragment);
  }
}

/**
 * Performance monitoring utilities
 */
export class PerformanceMonitor {
  private metrics = new Map<string, number>();
  private observers: PerformanceObserver[] = [];

  startTiming(name: string): void {
    this.metrics.set(name, performance.now());
  }

  endTiming(name: string): number {
    const startTime = this.metrics.get(name);
    if (startTime) {
      const duration = performance.now() - startTime;
      this.metrics.delete(name);
      return duration;
    }
    return 0;
  }

  measureNavigation(): PerformanceNavigationTiming | null {
    return performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming || null;
  }

  measurePaint(): { fcp?: number; lcp?: number } {
    const paintEntries = performance.getEntriesByType('paint');
    const fcp = paintEntries.find(entry => entry.name === 'first-contentful-paint')?.startTime;
    
    return {
      fcp,
      // LCP would need to be measured with PerformanceObserver
    };
  }

  startResourceObserver(callback: (entries: PerformanceResourceTiming[]) => void): void {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries() as PerformanceResourceTiming[];
        callback(entries);
      });
      
      observer.observe({ entryTypes: ['resource'] });
      this.observers.push(observer);
    }
  }

  disconnect(): void {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
    this.metrics.clear();
  }
}

/**
 * Memory optimization utilities
 */
export class MemoryManager {
  private objectRefs = new Set<any>();
  private cleanupTasks: (() => void)[] = [];

  addReference<T extends object>(obj: T): T {
    this.objectRefs.add(obj);
    return obj;
  }

  addCleanupTask(task: () => void): void {
    this.cleanupTasks.push(task);
  }

  cleanup(): void {
    // Run cleanup tasks
    this.cleanupTasks.forEach(task => {
      try {
        task();
      } catch (error) {
        console.warn('Cleanup task failed:', error);
      }
    });
    this.cleanupTasks = [];

    // Clear object references
    this.objectRefs.clear();
  }

  getMemoryUsage(): number | undefined {
    if ('memory' in performance) {
      return (performance as any).memory.usedJSHeapSize;
    }
    return undefined;
  }
}

/**
 * Bundle size analyzer for development
 */
export function analyzeBundleSize(): void {
  if (process.env.NODE_ENV === 'development') {
    console.group('📦 Bundle Analysis');
    
    // Analyze DOM size
    const elements = document.getElementsByTagName('*').length;
    console.log(`DOM elements: ${elements}`);
    
    // Analyze memory usage
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      console.log(`Memory usage: ${(memory.usedJSHeapSize / 1024 / 1024).toFixed(2)} MB`);
      console.log(`Memory limit: ${(memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2)} MB`);
    }
    
    // Analyze network resources
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    const totalSize = resources.reduce((acc, resource) => {
      return acc + (resource.transferSize || 0);
    }, 0);
    
    console.log(`Network resources: ${resources.length}`);
    console.log(`Total transfer size: ${(totalSize / 1024).toFixed(2)} KB`);
    
    console.groupEnd();
  }
}